import React, { useCallback, useMemo, useState } from "react";
import { PopupProps, Unstable_Popup as BasePopup } from "@mui/base/Unstable_Popup";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { useThemeContextSelector } from "@/utils/theme";
import Box from "@mui/material/Box";
import { icons } from "lucide-react";
import { usePrevious, useUpdateEffect } from "react-use";

export type PopoverProps = {
    children: (props: {
        openPopup: (event: React.MouseEvent<HTMLElement>) => void;
        closePopup: () => void;
    }) => React.ReactNode;
    content: React.ReactNode | ((props: { closePopup: () => void }) => React.ReactNode);
    PopupProps?: PopupProps;
    /** Callback called when the popup open is requested */
    onOpen?: () => void;
    /** Callback called when the popup close is requested */
    onClose?: () => void;
};

export const Popover = ({ children, content, PopupProps = {}, onOpen, onClose }: PopoverProps) => {
    const [anchor, setAnchor] = useState<null | HTMLElement>(null);

    const openPopup = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            setAnchor(anchor ? null : event.currentTarget);
        },
        [anchor]
    );

    const closePopup = useCallback(() => {
        setAnchor(null);
    }, []);

    const open = useMemo(() => Boolean(anchor), [anchor]);
    const prevOpen = usePrevious(open);

    useUpdateEffect(() => {
        if (open && !prevOpen) {
            onOpen?.();
        } else if (!open && prevOpen) {
            onClose?.();
        }
    }, [open, prevOpen]);

    const { slotProps = {}, ...basePopupProps } = PopupProps;

    const renderContent = useCallback(() => {
        return (
            <BasePopup
                disablePortal
                open={open}
                anchor={anchor}
                strategy="fixed"
                {...basePopupProps}
                slotProps={{
                    ...slotProps,
                    root: {
                        ...slotProps?.root,
                        style: {
                            zIndex: 10001,
                            ...slotProps?.root?.style,
                        },
                    },
                }}>
                {typeof content === "function" ? content({ closePopup }) : content}
            </BasePopup>
        );
        // biome-ignore lint: useExhaustiveDependencies
    }, [anchor, basePopupProps, closePopup, content, open, slotProps]);

    return (
        <ClickAwayListener onClickAway={() => setAnchor(null)}>
            <div data-testid="auto-id-b6f80e2d-d170-4024-b3f7-77e93a845459">
                {children({ openPopup, closePopup })}
                {renderContent()}
            </div>
        </ClickAwayListener>
    );
};

export const PopoverItem = ({
    label,
    icon,
    iconComponent,
    disabled,
    onClick,
    isActive,
}: {
    label: string | React.ReactNode;
    icon?: keyof typeof icons;
    iconComponent?: React.ReactNode;
    close?: boolean;
    disabled?: boolean;
    onClick: () => void;
    isActive?: boolean;
}) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);

    const style = {
        display: "flex",
        cursor: "pointer",
        border: "none",
        alignItems: "center",
        gap: "8px",
        padding: "6px",
        fontSize: "14px",
        fontWeight: 500,
        color: isDarkMode ? "#A3A3A3" : "#737373",
        textAlign: "left",
        backgroundColor: "transparent",
        width: "100%",
        borderRadius: "4px",
        ...(!isActive &&
            !disabled && {
                "&:hover": {
                    backgroundColor: isDarkMode ? "#171717" : "#F5F5F5",
                    color: isDarkMode ? "#E5E5E5" : "#262626",
                },
            }),
        ...(isActive &&
            !disabled && {
                backgroundColor: isDarkMode ? "#171717" : "#F5F5F5",
                color: isDarkMode ? "#E5E5E5" : "#262626",
            }),
        ...(disabled && {
            color: isDarkMode ? "#525252" : "#A3A3A3",
            cursor: "not-allowed",
        }),
    };

    const IconComponent = icon ? icons[icon] : null;

    const IconCustomComponent = iconComponent || null;

    return (
        <Box component={"button"} sx={style} onClick={onClick}>
            {IconComponent && <IconComponent style={{ width: "16px", height: "16px" }} />}
            {IconCustomComponent}
            {label}
        </Box>
    );
};
